import * as React from 'react';

import { Action, Dispatch } from 'redux';
import { CommandBarButton, IColumn, Image, ImageFit, Link, TextField, Spinner, SpinnerSize } from '@fluentui/react';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { setOnSiteEventsFilterText, setOnSiteManagerActiveKey, setPartnerActiveKey, setPartnerFilterText } from '../../redux/ui/actions';

import { AsyncData } from '../../redux/core';
import { Partner } from '../../redux/on-site-manager/partners/types';
import { PartnerDetailsList } from './PartnerDetailsList';
import { ROUTES } from '../../config/routes';
import { RootState } from '../../redux/types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { fetchPartners } from '../../redux/on-site-manager/partners/actions';
import TrieSearch from 'trie-search';
import { withRouter } from '../../util/Router';
import { NavigateFunction } from 'react-router-dom';

export interface OwnProps {
  navigate: NavigateFunction;
}

export interface StateProps {
  partnerActiveItem: string;
  partnerFilterText: string;
  partners: AsyncData<Partner[]>;
}

export interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    fetchPartners: () => void;
    setPartnerFilterText: (search: string) => void;
    setOnSiteManagerActiveKey: (key: string) => void;
    setOnSiteEventsFilterText: (search: string) => void;
    setPartnerActiveKey: (key: string) => void;
  };
}

type PartnersProps = OwnProps & StateProps & DispatchProps;

export interface PartnersState {
  items: Partner[];
  columns: IColumn[];
  selectedPartner?: Partner;
}

export class Component extends React.Component<PartnersProps, PartnersState> {
  private trie: any;

  constructor(props: PartnersProps) {
    super(props);
    this.state = {
      items: [],
      columns: this.buildColumns(),
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Partners');
    this.props.actions.fetchPartners();
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Partners');
  }

  public componentDidUpdate(prevProps: PartnersProps) {
    if (this.props.partners !== prevProps.partners) {
      this.trie = new TrieSearch(['name', 'contactName', 'contactEmail', 'fullAddress']);

      this.trie.addAll(this.props.partners.data || []);
      if (this.props.partnerFilterText) {
        this.setState({ items: this.trie.get(this.props.partnerFilterText) });
      } else {
        this.setState({ items: this.props.partners.data || [] });
      }
    }

    if (this.props.partnerFilterText !== prevProps.partnerFilterText) {
      if (this.props.partnerFilterText) {
        this.setState({ items: this.trie.get(this.props.partnerFilterText) });
      } else {
        this.setState({ items: this.props.partners.data || [] });
      }
    }
  }

  public render(): JSX.Element {
    return (
      <div>
        <div className="flex">
          <div className="flex" style={{ marginLeft: 'auto', marginRight: 10, height: 32 }}>
            <CommandBarButton iconProps={{ iconName: 'Add' }} text="Partner" onClick={() => this.props.navigate(ROUTES.onSiteManagerCreatePartner)} data-tid="btn_partner" />
          </div>
          <div style={{ marginRight: 10 }}>
            <TextField
              iconProps={{ iconName: 'Filter' }}
              placeholder="Filter partners"
              value={this.props.partnerFilterText}
              onChange={this.onSearchChange}
              data-tid="inp_filterPartners"
            />
          </div>
          <span style={{ fontSize: 20 }}>Results: {this.state.items.length}</span>
        </div>
        {this.renderPartnerDetailsList()}
      </div>
    );
  }

  private renderPartnerDetailsList(): JSX.Element {
    if (!this.props.partners.isFetching) {
      return (
        <PartnerDetailsList
          items={this.state.items}
          columns={this.state.columns}
          partnerActiveItem={this.props.partnerActiveItem!}
          setPartnerActiveKey={this.props.actions.setPartnerActiveKey}
        />
      );
    }
    return <Spinner size={SpinnerSize.large} />;
  }

  private onSearchChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    this.props.actions.setPartnerFilterText(newValue!);
  };

  private onOnSitesClick = (partnerName: string) => {
    this.props.actions.setOnSiteManagerActiveKey('onsites');
    this.props.actions.setOnSiteEventsFilterText(partnerName);
  };

  private buildColumns(): IColumn[] {
    return [
      {
        key: 'thumbnail',
        name: '',
        minWidth: 50,
        maxWidth: 50,
        onRender: (item: Partner) => {
          return <Image src={item.logoUrl} width={50} height={50} imageFit={ImageFit.contain} />;
        },
      },
      {
        key: 'name',
        name: 'Partner name',
        fieldName: 'name',
        minWidth: 100,
        maxWidth: 160,
        onRender: (item: Partner) => {
          const url = ROUTES.onSiteManagerEditPartner.replace(':id', item.id!);
          return <Link href={url}>{item.name}</Link>;
        },
      },
      {
        key: 'addressName',
        name: 'Campus name',
        fieldName: 'addressName',
        minWidth: 100,
        maxWidth: 200,
        isMultiline: true,
        isResizable: true,
        onRender: (item: Partner) => {
          if (item.addresses.length > 0) {
            return (
              <div>
                {item.addresses.map((a, i) => (
                  <div key={i}>{a.name}</div>
                ))}
              </div>
            );
          }
          return null;
        },
      },
      {
        key: 'fullAddress',
        name: 'Location',
        fieldName: 'fullAddress',
        minWidth: 120,
        maxWidth: 180,
        isMultiline: true,
        isResizable: true,
        onRender: (item: Partner) => {
          if (item.addresses.length > 0) {
            return (
              <div>
                {item.addresses.map((a, i) => (
                  <div className="overflow" key={i}>
                    {a.streetAddress}
                  </div>
                ))}
              </div>
            );
          }
          return null;
        },
      },
      {
        key: 'contactName',
        name: 'Contact name',
        fieldName: 'contactName',
        minWidth: 80,
        maxWidth: 140,
        isMultiline: true,
        isResizable: true,
        onRender: (partner: Partner) => {
          if (partner.addresses.length > 0) {
            return (
              <div>
                {partner.addresses.map((a, i) => (
                  <div className="overflow" key={i}>
                    {a.contactName}
                  </div>
                ))}
              </div>
            );
          }
          return null;
        },
      },
      {
        key: 'contactEmail',
        name: 'Contact email',
        fieldName: 'contactEmail',
        minWidth: 80,
        maxWidth: 140,
        isMultiline: true,
        isResizable: true,
        onRender: (partner: Partner) => {
          if (partner.addresses.length > 0) {
            return (
              <div>
                {partner.addresses.map((a, i) => (
                  <div className="overflow" key={i}>
                    {a.contactEmail}
                  </div>
                ))}
              </div>
            );
          }
          return null;
        },
      },
      {
        key: 'onSites',
        name: 'Upcoming On-Sites',
        fieldName: 'onSites',
        minWidth: 80,
        maxWidth: 80,
        onRender: (partner: Partner) => {
          if (partner.onsites) {
            return <Link onClick={() => this.onOnSitesClick(partner.name)}>{partner.onsites}</Link>;
          }
          return <div>0</div>;
        },
      },
    ];
  }
}

export function mapStateToProps(state: RootState): StateProps {
  return {
    partners: state.onSiteManager.partners.partners,
    partnerFilterText: state.ui.partnerFilterText,
    partnerActiveItem: state.ui.partnerActiveItem,
  };
}

export function mapDispatchToProps(dispatch: Dispatch<Action>): DispatchProps {
  return {
    actions: bindActionCreators(
      {
        onPageDidMount,
        onPageWillUnmount,
        fetchPartners,
        setPartnerFilterText,
        setOnSiteEventsFilterText,
        setOnSiteManagerActiveKey,
        setPartnerActiveKey,
      },
      dispatch
    ),
  };
}

export const Partners = withRouter(connect(mapStateToProps, mapDispatchToProps)(Component));
